import { z } from 'zod';
import { CountryCodeSchema, type CountryCode } from './geo';

/* ------------------------------------------------------------------ */
/* Payments, Capsico Money, refunds, payouts, tax invoices (W11)       */
/*                                                                     */
/* Online orders are held away from the kitchen until the gateway      */
/* confirms the money (server-to-server — never on the app's word).    */
/* Capsico Money is store credit only: no top-ups, every credit        */
/* expires. Refunds for system or restaurant failures go to Capsico    */
/* Money instantly; approved complaints let the customer choose        */
/* Capsico Money (+ a bonus) or the original method. Cash is never     */
/* refunded as cash. Restaurants get a nightly statement; Ops download */
/* a bank file and mark it paid.                                       */
/* ------------------------------------------------------------------ */

/* ---------------- payment ---------------- */

/**
 * How the customer pays (W11). Online methods go through a gateway (see payments.ts):
 * India — UPI, cards, netbanking through Razorpay; Nepal — eSewa, Khalti, Fonepay QR, IME Pay,
 * ConnectIPS. "wallet" = the whole bill is covered by Capsico Money. "test" is development only.
 */
export const PAYMENT_METHODS = [
  'cod',
  'test',
  'wallet',
  'upi',
  'card',
  'netbanking',
  'esewa',
  'khalti',
  'fonepay',
  'imepay',
  'connectips',
] as const;
export const PaymentMethodSchema = z.enum(PAYMENT_METHODS);
export type PaymentMethod = z.infer<typeof PaymentMethodSchema>;

export const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
  cod: 'Cash on delivery',
  test: 'Test payment',
  wallet: 'Capsico Money',
  upi: 'UPI',
  card: 'Credit / debit card',
  netbanking: 'Netbanking',
  esewa: 'eSewa',
  khalti: 'Khalti',
  fonepay: 'Fonepay QR',
  imepay: 'IME Pay',
  connectips: 'ConnectIPS',
};

/** Methods that need a payment gateway before the kitchen sees the order. */
export const ONLINE_METHODS = [
  'upi',
  'card',
  'netbanking',
  'esewa',
  'khalti',
  'fonepay',
  'imepay',
  'connectips',
] as const satisfies readonly PaymentMethod[];
export type OnlineMethod = (typeof ONLINE_METHODS)[number];
export function isOnlineMethod(method: PaymentMethod): method is OnlineMethod {
  return (ONLINE_METHODS as readonly string[]).includes(method);
}

/**
 * awaiting_payment — sent to a gateway, the kitchen does not see it yet
 * cod_due          — cash on delivery, collected at the door
 * paid             — fully paid (gateway, Capsico Money or test)
 * partly_refunded / refunded — money went back (Capsico Money or the original method)
 * failed           — the payment never completed; the order was cancelled
 */
export const PAYMENT_STATUSES = [
  'awaiting_payment',
  'cod_due',
  'paid',
  'partly_refunded',
  'refunded',
  'failed',
] as const;
export const PaymentStatusSchema = z.enum(PAYMENT_STATUSES);
export type PaymentStatus = z.infer<typeof PaymentStatusSchema>;

/** Cash on delivery trust tier: new customers get a low cash limit, risky ones none. */
export const COD_TIERS = ['new', 'trusted', 'risky'] as const;
export type CodTier = (typeof COD_TIERS)[number];

/* ---------------- gateways ---------------- */

export const GATEWAYS = ['razorpay', 'esewa', 'khalti', 'fonepay', 'imepay', 'connectips'] as const;
export const GatewaySchema = z.enum(GATEWAYS);
export type Gateway = z.infer<typeof GatewaySchema>;
export const GATEWAY_LABELS: Record<Gateway, string> = {
  razorpay: 'Razorpay',
  esewa: 'eSewa',
  khalti: 'Khalti',
  fonepay: 'Fonepay',
  imepay: 'IME Pay',
  connectips: 'ConnectIPS',
};

/** Which gateway takes each online method. */
export const METHOD_GATEWAY: Record<OnlineMethod, Gateway> = {
  upi: 'razorpay',
  card: 'razorpay',
  netbanking: 'razorpay',
  esewa: 'esewa',
  khalti: 'khalti',
  fonepay: 'fonepay',
  imepay: 'imepay',
  connectips: 'connectips',
};

/** Methods offered per country, in display order (Capsico Money is applied separately). */
export const COUNTRY_ONLINE_METHODS: Record<CountryCode, readonly OnlineMethod[]> = {
  IN: ['upi', 'card', 'netbanking'],
  NP: ['esewa', 'khalti', 'fonepay', 'imepay', 'connectips'],
};

export const CURRENCY: Record<CountryCode, 'INR' | 'NPR'> = { IN: 'INR', NP: 'NPR' };

/**
 * What the app does to hand the customer to the gateway. Never carries a secret: Razorpay's
 * key id is public by design, form fields are signed by the server.
 */
export const PaymentSessionSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('razorpay_checkout'),
    keyId: z.string(),
    orderId: z.string(),
    amountMinor: z.number().int(),
    currency: z.string(),
    name: z.string(),
    description: z.string(),
    prefillContact: z.string().nullable(),
    method: z.enum(['upi', 'card', 'netbanking']),
  }),
  /** POST these fields to `action` (eSewa, ConnectIPS). */
  z.object({
    kind: z.literal('form_post'),
    action: z.url(),
    fields: z.record(z.string(), z.string()),
  }),
  /** Open this URL (Khalti, IME Pay). */
  z.object({ kind: z.literal('redirect'), url: z.url() }),
  /** Show this QR and wait (Fonepay). */
  z.object({ kind: z.literal('qr'), payload: z.string(), amountMinor: z.number().int() }),
]);
export type PaymentSession = z.infer<typeof PaymentSessionSchema>;

export const INTENT_STATUSES = ['pending', 'paid', 'failed', 'expired', 'cancelled'] as const;
export const IntentStatusSchema = z.enum(INTENT_STATUSES);
export type IntentStatus = z.infer<typeof IntentStatusSchema>;

export const PaymentIntentViewSchema = z.object({
  id: z.uuid(),
  orderId: z.uuid(),
  gateway: GatewaySchema,
  method: PaymentMethodSchema,
  amountMinor: z.number().int(),
  status: IntentStatusSchema,
  /** Present while the intent is pending. */
  session: PaymentSessionSchema.nullable(),
  expiresAt: z.string(),
  failure: z.string().nullable(),
});
export type PaymentIntentView = z.infer<typeof PaymentIntentViewSchema>;

/** The app came back from the gateway: whatever the gateway put in the return URL. */
export const ConfirmPaymentInputSchema = z.object({
  params: z
    .record(z.string().max(60), z.string().max(4000))
    .refine((value) => Object.keys(value).length <= 30, 'Too many fields'),
});
export type ConfirmPaymentInput = z.infer<typeof ConfirmPaymentInputSchema>;

/** Try again with the same or another online method (same amount). */
export const RetryPaymentInputSchema = z.object({ method: PaymentMethodSchema });
export type RetryPaymentInput = z.infer<typeof RetryPaymentInputSchema>;

/* ---------------- refunds ---------------- */

export const REFUND_REASONS = [
  'restaurant_rejected',
  'not_accepted',
  'ops_cancelled',
  'customer_cancelled',
  'payment_timeout',
  'complaint',
  'goodwill',
] as const;
export const RefundReasonSchema = z.enum(REFUND_REASONS);
export type RefundReason = z.infer<typeof RefundReasonSchema>;
export const REFUND_REASON_LABELS: Record<RefundReason, string> = {
  restaurant_rejected: 'Restaurant could not take the order',
  not_accepted: 'Restaurant did not accept in time',
  ops_cancelled: 'Cancelled by Capsico',
  customer_cancelled: 'You cancelled the order',
  payment_timeout: 'Payment did not finish in time',
  complaint: 'Complaint approved',
  goodwill: 'Goodwill credit',
};

export const REFUND_DESTINATIONS = ['wallet', 'source'] as const;
export const RefundDestinationSchema = z.enum(REFUND_DESTINATIONS);
export type RefundDestination = z.infer<typeof RefundDestinationSchema>;

/**
 * awaiting_choice — complaint refund waiting for the customer to pick (defaults to Capsico Money)
 * processing      — sent to the gateway
 * manual          — the gateway has no refund API: Ops refund it from the merchant panel
 * done / failed
 */
export const REFUND_STATUSES = [
  'awaiting_choice',
  'processing',
  'manual',
  'done',
  'failed',
] as const;
export const RefundStatusSchema = z.enum(REFUND_STATUSES);
export type RefundStatus = z.infer<typeof RefundStatusSchema>;

export const FAULTS = ['restaurant', 'capsico', 'rider'] as const;
export const FaultSchema = z.enum(FAULTS);
export type Fault = z.infer<typeof FaultSchema>;

export const RefundViewSchema = z.object({
  id: z.uuid(),
  orderId: z.uuid(),
  amountMinor: z.number().int(),
  /** Extra Capsico Money for choosing it (complaints only). */
  bonusMinor: z.number().int(),
  reason: RefundReasonSchema,
  destination: RefundDestinationSchema.nullable(),
  status: RefundStatusSchema,
  /** The original method can be chosen (false for cash and Capsico Money). */
  sourceAllowed: z.boolean(),
  /** Until when the customer can pick; then it goes to Capsico Money. */
  chooseBy: z.string().nullable(),
  createdAt: z.string(),
  doneAt: z.string().nullable(),
});
export type RefundView = z.infer<typeof RefundViewSchema>;

export const RefundChoiceInputSchema = z.object({ destination: RefundDestinationSchema });
export type RefundChoiceInput = z.infer<typeof RefundChoiceInputSchema>;

/* ---------------- tax invoices ---------------- */

export const INVOICE_KINDS = ['food', 'fees'] as const;
export const InvoiceKindSchema = z.enum(INVOICE_KINDS);
export type InvoiceKind = z.infer<typeof InvoiceKindSchema>;
export const INVOICE_KIND_LABELS: Record<InvoiceKind, string> = {
  food: 'Food invoice',
  fees: 'Capsico fees invoice',
};

export const CustomerInvoiceViewSchema = z.object({
  id: z.uuid(),
  kind: InvoiceKindSchema,
  number: z.string(),
  issuedAt: z.string(),
  sellerName: z.string(),
  totalMinor: z.number().int(),
});
export type CustomerInvoiceView = z.infer<typeof CustomerInvoiceViewSchema>;

/* ---------------- order payment view ---------------- */

export const OrderPaymentViewSchema = z.object({
  method: PaymentMethodSchema,
  status: PaymentStatusSchema,
  /** Paid online (or due at the door for cash). */
  payableMinor: z.number().int(),
  walletMinor: z.number().int(),
  refundedMinor: z.number().int(),
  /** The open attempt while the order waits for payment. */
  intent: PaymentIntentViewSchema.nullable(),
  /** Online methods the customer can switch to while waiting. */
  retryMethods: z.array(PaymentMethodSchema),
  refunds: z.array(RefundViewSchema),
  invoices: z.array(CustomerInvoiceViewSchema),
});
export type OrderPaymentView = z.infer<typeof OrderPaymentViewSchema>;

/* ---------------- Capsico Money ---------------- */

export const WALLET_SOURCES = [
  'refund',
  'complaint_bonus',
  'goodwill',
  'compensation',
  'referral',
  'cashback',
  'winback',
] as const;
export const WalletSourceSchema = z.enum(WALLET_SOURCES);
export type WalletSource = z.infer<typeof WalletSourceSchema>;
export const WALLET_SOURCE_LABELS: Record<WalletSource, string> = {
  refund: 'Refund',
  complaint_bonus: 'Bonus for choosing Capsico Money',
  goodwill: 'From Capsico',
  compensation: 'Sorry credit',
  referral: 'Refer-a-friend reward',
  cashback: 'Cashback',
  winback: 'Welcome-back credit',
};

export const WALLET_ENTRIES = ['credit', 'debit', 'reverse', 'expire'] as const;
export type WalletEntry = (typeof WALLET_ENTRIES)[number];

export const WalletCreditSchema = z.object({
  id: z.uuid(),
  source: WalletSourceSchema,
  amountMinor: z.number().int(),
  remainingMinor: z.number().int(),
  note: z.string().nullable(),
  expiresAt: z.string(),
  createdAt: z.string(),
});
export type WalletCredit = z.infer<typeof WalletCreditSchema>;

export const WalletLedgerRowSchema = z.object({
  id: z.uuid(),
  entry: z.enum(WALLET_ENTRIES),
  /** Signed: credits positive, debits and expiries negative. */
  amountMinor: z.number().int(),
  balanceAfterMinor: z.number().int(),
  note: z.string().nullable(),
  orderNumber: z.string().nullable(),
  at: z.string(),
});
export type WalletLedgerRow = z.infer<typeof WalletLedgerRowSchema>;

export const WalletViewSchema = z.object({
  country: CountryCodeSchema,
  balanceMinor: z.number().int(),
  /** Live credits, soonest expiry first. */
  credits: z.array(WalletCreditSchema),
  ledger: z.array(WalletLedgerRowSchema),
  /** Refunds waiting for the customer to pick where the money goes. */
  pendingChoices: z.array(RefundViewSchema.extend({ orderNumber: z.string() })),
});
export type WalletView = z.infer<typeof WalletViewSchema>;

export const WalletQuerySchema = z.object({ country: CountryCodeSchema.default('IN') });
export type WalletQuery = z.infer<typeof WalletQuerySchema>;

/* ---------------- Capsico OS: refunds ---------------- */

export const RefundRowSchema = RefundViewSchema.extend({
  orderNumber: z.string(),
  country: CountryCodeSchema,
  outletName: z.string(),
  customerPhoneMasked: z.string().nullable(),
  paymentMethod: PaymentMethodSchema,
  gateway: GatewaySchema.nullable(),
  fault: FaultSchema.nullable(),
  gatewayRefundRef: z.string().nullable(),
  note: z.string().nullable(),
  failure: z.string().nullable(),
  requestedBy: z.string(),
  version: z.number().int(),
});
export type RefundRow = z.infer<typeof RefundRowSchema>;

export const RefundListQuerySchema = z.object({
  country: CountryCodeSchema.optional(),
  status: RefundStatusSchema.optional(),
  search: z.string().trim().max(40).optional(),
});
export type RefundListQuery = z.infer<typeof RefundListQuerySchema>;

export const RefundListSchema = z.object({
  rows: z.array(RefundRowSchema),
  counts: z.record(RefundStatusSchema, z.number().int()),
});
export type RefundList = z.infer<typeof RefundListSchema>;

const note = z
  .string()
  .trim()
  .max(300)
  .nullable()
  .default(null)
  .transform((value) => (value === '' ? null : value));

/** An approved complaint (or goodwill) refund for a delivered order. */
export const CreateRefundInputSchema = z.object({
  orderId: z.uuid(),
  amountMinor: z.number().int().min(100),
  reason: z.enum(['complaint', 'goodwill']),
  fault: FaultSchema,
  note: z.string().trim().min(5, 'Say why (at least 5 letters)').max(300),
});
export type CreateRefundInput = z.infer<typeof CreateRefundInputSchema>;

/** Ops refunded it from the gateway's merchant panel. */
export const CompleteRefundInputSchema = z.object({
  expectedVersion: z.number().int().min(1),
  reference: z.string().trim().min(3).max(80),
  note,
});
export type CompleteRefundInput = z.infer<typeof CompleteRefundInputSchema>;

export const RetryRefundInputSchema = z.object({ expectedVersion: z.number().int().min(1) });
export type RetryRefundInput = z.infer<typeof RetryRefundInputSchema>;

/* ---------------- Capsico OS: restaurant payouts ---------------- */

/**
 * ready    — built tonight, waiting for a bank file
 * in_batch — in a downloaded bank file, waiting to be marked paid
 * paid     — money sent
 * held     — Ops held it (dispute, missing bank details)
 * carried  — the net was below the minimum or negative; it rolls into the next statement
 */
export const STATEMENT_STATUSES = ['ready', 'in_batch', 'paid', 'held', 'carried'] as const;
export const StatementStatusSchema = z.enum(STATEMENT_STATUSES);
export type StatementStatus = z.infer<typeof StatementStatusSchema>;

export const StatementMoneySchema = z.object({
  orders: z.number().int(),
  /** Food the restaurant sold (item prices). */
  foodMinor: z.number().int(),
  packagingMinor: z.number().int(),
  /** Restaurant-funded part of offers (taken off). */
  restaurantDiscountMinor: z.number().int(),
  /** Missed-order and other penalties. */
  penaltiesMinor: z.number().int(),
  /** Plan / overage bills taken from this payout. */
  subscriptionMinor: z.number().int(),
  /** Complaint refunds the restaurant was at fault for. */
  refundsMinor: z.number().int(),
  /** Income tax withheld (India 194-O TDS). */
  tdsMinor: z.number().int(),
  /** Negative or small balance from the previous statement. */
  carriedInMinor: z.number().int(),
  netMinor: z.number().int(),
});
export type StatementMoney = z.infer<typeof StatementMoneySchema>;

export const PayoutStatementRowSchema = StatementMoneySchema.extend({
  id: z.uuid(),
  outletId: z.uuid(),
  outletName: z.string(),
  restaurantName: z.string(),
  country: CountryCodeSchema,
  businessDate: z.string(),
  status: StatementStatusSchema,
  batchId: z.uuid().nullable(),
  paidAt: z.string().nullable(),
  paidReference: z.string().nullable(),
  holdReason: z.string().nullable(),
  /** Bank account on file (last 4 only). */
  bankLast4: z.string().nullable(),
  bankReady: z.boolean(),
  version: z.number().int(),
});
export type PayoutStatementRow = z.infer<typeof PayoutStatementRowSchema>;

export const StatementOrderSchema = z.object({
  orderId: z.uuid(),
  number: z.string(),
  deliveredAt: z.string().nullable(),
  status: z.string(),
  foodMinor: z.number().int(),
  packagingMinor: z.number().int(),
  restaurantDiscountMinor: z.number().int(),
  penaltyMinor: z.number().int(),
  netMinor: z.number().int(),
});
export type StatementOrder = z.infer<typeof StatementOrderSchema>;

export const PayoutStatementDetailSchema = PayoutStatementRowSchema.extend({
  orders: z.array(StatementOrderSchema),
  deductions: z.array(z.object({ label: z.string(), amountMinor: z.number().int() })),
});
export type PayoutStatementDetail = z.infer<typeof PayoutStatementDetailSchema>;

export const PayoutQuerySchema = z.object({
  country: CountryCodeSchema.optional(),
  status: StatementStatusSchema.optional(),
  date: z.iso.date().optional(),
  search: z.string().trim().max(40).optional(),
});
export type PayoutQuery = z.infer<typeof PayoutQuerySchema>;

export const BATCH_STATUSES = ['open', 'paid'] as const;
export type BatchStatus = (typeof BATCH_STATUSES)[number];

export const PayoutBatchRowSchema = z.object({
  id: z.uuid(),
  country: CountryCodeSchema,
  statements: z.number().int(),
  totalMinor: z.number().int(),
  status: z.enum(BATCH_STATUSES),
  createdAt: z.string(),
  createdBy: z.string(),
  paidAt: z.string().nullable(),
  reference: z.string().nullable(),
});
export type PayoutBatchRow = z.infer<typeof PayoutBatchRowSchema>;

export const PayoutListSchema = z.object({
  rows: z.array(PayoutStatementRowSchema),
  counts: z.record(StatementStatusSchema, z.number().int()),
  totals: z.object({ readyMinor: z.number().int(), heldMinor: z.number().int() }),
  batches: z.array(PayoutBatchRowSchema),
});
export type PayoutList = z.infer<typeof PayoutListSchema>;

export const RunStatementsInputSchema = z.object({
  country: CountryCodeSchema,
  /** Local business date; defaults to today in the country's time zone. */
  businessDate: z.iso.date().nullable().default(null),
});
export type RunStatementsInput = z.infer<typeof RunStatementsInputSchema>;

export const CreateBatchInputSchema = z.object({
  country: CountryCodeSchema,
  statementIds: z.array(z.uuid()).min(1).max(500),
});
export type CreateBatchInput = z.infer<typeof CreateBatchInputSchema>;

export const MarkBatchPaidInputSchema = z.object({
  reference: z.string().trim().min(3, 'Add the bank reference').max(80),
});
export type MarkBatchPaidInput = z.infer<typeof MarkBatchPaidInputSchema>;

export const HoldStatementInputSchema = z.object({
  expectedVersion: z.number().int().min(1),
  hold: z.boolean(),
  reason: note,
});
export type HoldStatementInput = z.infer<typeof HoldStatementInputSchema>;

/* ---------------- helpers ---------------- */

/** Capsico Money to use: never more than the bill, never more than the balance. */
export function walletApply(balanceMinor: number, totalMinor: number, use: boolean): number {
  if (!use) return 0;
  return Math.max(0, Math.min(balanceMinor, totalMinor));
}
